import { useEditor, EditorContent, Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Subscript from '@tiptap/extension-subscript';
import Superscript from '@tiptap/extension-superscript';
import { Paper, IconButton, Stack, Box } from '@mui/material';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import FormatStrikethroughIcon from '@mui/icons-material/StrikethroughS';
import SuperscriptIcon from '@mui/icons-material/Superscript';
import SubscriptIcon from '@mui/icons-material/Subscript';

type Props = {
  value: string;
  onChange: (content: string) => void;
};

export function RichTextEditor({ value, onChange }: Props) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        // Disable underline in StarterKit if it exists, since we're adding it separately
        underline: false,
      }),
      Underline,
      Subscript,
      Superscript,
    ],
    content: value,
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
    immediatelyRender: false,
    editorProps: {
      attributes: {
        style: 'min-height: 2.5em; padding: 8px; outline: none;',
      },
    },
  });

  if (!editor) return null;

  return (
    <Paper variant="outlined" sx={{ p: 1 }}>
      <Toolbar editor={editor} />
      <EditorContent
        editor={editor}
        style={{
          minHeight: '2.5em',
          padding: '8px',
        }}
      />
    </Paper>
  );
}

type FormatAction = (editor: Editor) => void;

// Format actions map
const FormatActions: Record<string, FormatAction> = {
  bold: (editor) => editor.chain().focus().toggleBold().run(),
  italic: (editor) => editor.chain().focus().toggleItalic().run(),
  underline: (editor) => editor.chain().focus().toggleUnderline().run(),
  strike: (editor) => editor.chain().focus().toggleStrike().run(),
  superScript: (editor) => editor.chain().focus().toggleSuperscript().run(),
  subScript: (editor) => editor.chain().focus().toggleSubscript().run(),
};

// Toolbar with dynamic buttons
function Toolbar({ editor }: { editor: Editor }) {
  return (
    <Box
      sx={{
        display: 'inline-flex', // Toolbar only takes up as much space as the buttons
        mb: 0.5,
        backgroundColor: 'background.paper', // Background color for toolbar
        borderRadius: 0.5, // Smaller rounded corners
        padding: '4px', // Reduced padding around the toolbar buttons
        boxShadow: 0.5, // Lighter shadow
        border: '1px solid', // Border around toolbar
        borderColor: 'divider', // Use MUI divider color
      }}
    >
      <Stack direction="row" spacing={0.5}>
        {Object.entries(FormatActions).map(([format, action]) => (
          <IconButton
            key={format}
            onClick={() => action(editor)}
            color={editor.isActive(format) ? 'primary' : 'default'}
            size="small"
            sx={{
              borderRadius: '2px',
              padding: '2px',
              minWidth: '24px',
              height: '24px',
            }}
          >
            {/* Render icons for each format */}
            {format === 'bold' ? <FormatBoldIcon fontSize="small" /> : null}
            {format === 'italic' ? <FormatItalicIcon fontSize="small" /> : null}
            {format === 'underline' ? <FormatUnderlinedIcon fontSize="small" /> : null}
            {format === 'strike' ? <FormatStrikethroughIcon fontSize="small" /> : null}
            {format === 'superScript' ? <SuperscriptIcon fontSize="small" /> : null}
            {format === 'subScript' ? <SubscriptIcon fontSize="small" /> : null}
          </IconButton>
        ))}
      </Stack>
    </Box>
  );
}
